import React, { useState } from 'react';
import { motion } from 'framer-motion';

interface EventTemplate {
  name: string;
  type: string;
  template: any;
}

const eventTemplates: EventTemplate[] = [
  {
    name: 'Content Submission',
    type: 'content_submission',
    template: {
      title: 'Sample Article Title',
      body: 'This is the article content that needs to be enhanced...',
      author: 'John Doe',
      category: 'Technology'
    }
  },
  {
    name: 'Data Import',
    type: 'data_import',
    template: {
      records: [
        { id: 1, name: 'Item 1', value: 100 },
        { id: 2, name: 'Item 2', value: 200 }
      ],
      source: 'CSV Import',
      timestamp: new Date().toISOString()
    }
  },
  {
    name: 'User Feedback',
    type: 'user_feedback',
    template: {
      userId: 'user123',
      feedback: 'This product is great but could use some improvements...',
      rating: 4,
      timestamp: new Date().toISOString()
    }
  },
  {
    name: 'API Request',
    type: 'api_request',
    template: {
      endpoint: '/api/process',
      method: 'POST',
      data: { key: 'value' },
      headers: { 'Content-Type': 'application/json' }
    }
  }
];

export const EventIngestion: React.FC = () => {
  const [source, setSource] = useState('manual');
  const [eventType, setEventType] = useState('content_submission');
  const [payload, setPayload] = useState('{}');
  const [metadata, setMetadata] = useState('{}');
  const [sending, setSending] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleTemplateSelect = (template: EventTemplate) => {
    setEventType(template.type);
    setPayload(JSON.stringify(template.template, null, 2));
    setMetadata(JSON.stringify({
      template: template.name,
      timestamp: new Date().toISOString()
    }, null, 2));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    setResponse(null);

    try {
      // Validate JSON
      const parsedPayload = JSON.parse(payload);
      const parsedMetadata = metadata ? JSON.parse(metadata) : {};

      const res = await fetch('/api/ahtl/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source,
          type: eventType,
          payload: parsedPayload,
          metadata: parsedMetadata
        })
      });

      const data = await res.json();
      
      if (res.ok) {
        setResponse(data);
        // Reset form
        setTimeout(() => {
          setPayload('{}');
          setMetadata('{}');
        }, 2000);
      } else {
        setError(data.error || 'Failed to ingest event');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid JSON format');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          📥 Event Ingestion
        </h1>
        <p className="text-gray-600">
          Submit events to the AI Adjuster / Human Taster Loop pipeline
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Templates */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Quick Templates
            </h2>
            <div className="space-y-2">
              {eventTemplates.map((template) => (
                <button
                  key={template.type}
                  onClick={() => handleTemplateSelect(template)}
                  className="w-full text-left p-3 rounded-lg border hover:bg-blue-50 hover:border-blue-300 transition-colors"
                >
                  <div className="font-medium text-gray-900">{template.name}</div>
                  <div className="text-sm text-gray-500">{template.type}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Recent Events */}
          <div className="bg-white rounded-lg shadow-sm border p-4 mt-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Event Sources
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Manual</span>
                <span className="font-medium">Active</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Webhook</span>
                <span className="text-gray-400">Configure</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">API</span>
                <span className="text-gray-400">Configure</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Schedule</span>
                <span className="text-gray-400">Configure</span>
              </div>
            </div>
          </div>
        </div>

        {/* Event Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Event Details
            </h2>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Source
                </label>
                <input
                  type="text"
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Type
                </label>
                <input
                  type="text"
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payload (JSON)
              </label>
              <textarea
                value={payload}
                onChange={(e) => setPayload(e.target.value)}
                className="w-full h-48 px-3 py-2 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Metadata (JSON, Optional)
              </label>
              <textarea
                value={metadata}
                onChange={(e) => setMetadata(e.target.value)}
                className="w-full h-24 px-3 py-2 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Response/Error Display */}
            {response && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg"
              >
                <div className="flex items-center space-x-2 text-green-800">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Event ingested successfully!</span>
                </div>
                <div className="mt-2 text-sm text-green-700">
                  Event ID: <code className="bg-green-100 px-2 py-1 rounded">{response.id}</code>
                </div>
              </motion.div>
            )}

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg"
              >
                <div className="flex items-center space-x-2 text-red-800">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Error:</span>
                  <span>{error}</span>
                </div>
              </motion.div>
            )}

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={sending}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors font-medium"
              >
                {sending ? 'Sending...' : 'Ingest Event'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPayload('{}');
                  setMetadata('{}');
                  setResponse(null);
                  setError(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Clear
              </button>
            </div>
          </form>

          {/* Processing Pipeline Visualization */}
          <div className="bg-white rounded-lg shadow-sm border p-6 mt-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Processing Pipeline
            </h2>
            <div className="flex items-center justify-between">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-xl">📥</span>
                </div>
                <span className="text-xs mt-2">Ingest</span>
              </div>
              <div className="flex-1 h-0.5 bg-gray-300 mx-2"></div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-xl">🔄</span>
                </div>
                <span className="text-xs mt-2">Normalize</span>
              </div>
              <div className="flex-1 h-0.5 bg-gray-300 mx-2"></div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                  <span className="text-xl">🤖</span>
                </div>
                <span className="text-xs mt-2">AI Process</span>
              </div>
              <div className="flex-1 h-0.5 bg-gray-300 mx-2"></div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="text-xl">📊</span>
                </div>
                <span className="text-xs mt-2">Score</span>
              </div>
              <div className="flex-1 h-0.5 bg-gray-300 mx-2"></div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <span className="text-xl">👤</span>
                </div>
                <span className="text-xs mt-2">Review</span>
              </div>
              <div className="flex-1 h-0.5 bg-gray-300 mx-2"></div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-xl">🚀</span>
                </div>
                <span className="text-xs mt-2">Publish</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};